import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api, getErrorMessage } from '../lib/api';
import {
  CreditCard,
  Search,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Building2,
  Box,
  Layers,
  ExternalLink,
  Activity,
  AlertCircle,
  AlertTriangle,
  FileText,
  ShieldAlert,
  Eye,
  Check,
  Ban,
  RotateCcw,
  X,
  Calendar,
  DollarSign,
  User,
  Info,
  FileCheck,
} from 'lucide-react';
import { SubscriptionReceiptModal } from '../components/SubscriptionReceiptModal';

interface AdminSubscriptionItem {
  id: string;
  client_id: string;
  product_id: string;
  plan_id: string;
  application_id?: string | null;
  status: 'active' | 'cancelled' | 'expired';
  payment_status?: 'pending' | 'pending_approval' | 'paid' | 'failed';
  payment_method_id?: string | null;
  payment_details?: Record<string, any>;
  payment_method?: {
    id: string;
    name: string;
    type: string;
  } | null;
  start_date: string;
  end_date?: string | null;
  created_at: string;
  updated_at?: string;
  client?: {
    id: string;
    company_name: string;
    email: string;
  } | null;
  product?: {
    id: string;
    name: string;
    description?: string;
    authentication_provider?: string;
  };
  plan?: {
    tier: string;
    monthly_quota: number;
    rate_limit_per_minute: number;
    monthly_price: number;
    per_transaction_price?: number;
    pricing_model?: string;
  };
  application?: {
    id: string;
    name: string;
    sandbox_mode: boolean;
  } | null;
}

export const SubscriptionsManagement: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<AdminSubscriptionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successBanner, setSuccessBanner] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [envFilter, setEnvFilter] = useState<'all' | 'sandbox' | 'production'>('all');

  // Modal states
  const [selectedSub, setSelectedSub] = useState<AdminSubscriptionItem | null>(null);
  const [receiptSub, setReceiptSub] = useState<AdminSubscriptionItem | null>(null);
  const [activeModal, setActiveModal] = useState<
    'approve' | 'deactivate' | 'reactivate' | 'details' | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [deactivationReason, setDeactivationReason] = useState('Inadimplência / Falta de Pagamento');

  useEffect(() => {
    fetchSubscriptions();
  }, [statusFilter, paymentStatusFilter]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError('');
      const params: Record<string, string | number> = { limit: 100 };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (paymentStatusFilter !== 'all') {
        params.payment_status = paymentStatusFilter;
      }
      const response = await api.get('/admin/subscriptions', { params });
      setSubscriptions(response.data.data || []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter((sub) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        (sub.client?.company_name || '').toLowerCase().includes(q) ||
        (sub.client?.email || '').toLowerCase().includes(q) ||
        (sub.product?.name || '').toLowerCase().includes(q) ||
        (sub.application?.name || '').toLowerCase().includes(q) ||
        (sub.payment_method?.name || '').toLowerCase().includes(q) ||
        (sub.plan?.tier || '').toLowerCase().includes(q);

      const matchesEnv =
        envFilter === 'all' ||
        (envFilter === 'sandbox' && sub.application?.sandbox_mode === true) ||
        (envFilter === 'production' && sub.application?.sandbox_mode === false);

      return matchesSearch && matchesEnv;
    });
  }, [subscriptions, searchTerm, envFilter]);

  const stats = useMemo(() => {
    const total = subscriptions.length;
    const active = subscriptions.filter((s) => s.status === 'active').length;
    const pendingApprovals = subscriptions.filter(
      (s) => s.payment_status === 'pending_approval' || s.payment_status === 'pending'
    ).length;
    const connectedApps = new Set(subscriptions.map((s) => s.application_id).filter(Boolean)).size;
    const mrr = subscriptions
      .filter((s) => s.status === 'active')
      .reduce((acc, s) => acc + Number(s.plan?.monthly_price || 0), 0);
    return { total, active, pendingApprovals, connectedApps, mrr };
  }, [subscriptions]);

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('pt-AO', {
      style: 'currency',
      currency: 'AOA',
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  const openModal = (
    sub: AdminSubscriptionItem,
    type: 'approve' | 'deactivate' | 'reactivate' | 'details'
  ) => {
    setSelectedSub(sub);
    setActiveModal(type);
    setAdminNotes('');
    setDeactivationReason('Inadimplência / Falta de Pagamento');
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedSub(null);
    setAdminNotes('');
  };

  // Actions
  const handleApprovePayment = async () => {
    if (!selectedSub) return;
    try {
      setActionLoading(true);
      setError('');
      await api.patch(`/admin/subscriptions/${selectedSub.id}/admin-status`, {
        status: 'active',
        payment_status: 'paid',
        admin_notes: adminNotes || 'Pagamento confirmado manualmente por administrador',
      });
      setSuccessBanner(
        `Pagamento da subscrição de ${selectedSub.client?.company_name || 'Cliente'} aprovado com sucesso! Acesso Kong ativado.`
      );
      closeModal();
      await fetchSubscriptions();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selectedSub) return;
    try {
      setActionLoading(true);
      setError('');
      await api.patch(`/admin/subscriptions/${selectedSub.id}/admin-status`, {
        status: 'cancelled',
        reason: deactivationReason,
        admin_notes: adminNotes,
      });
      setSuccessBanner(
        `Subscrição de ${selectedSub.client?.company_name || 'Cliente'} desativada com sucesso. Acesso Kong revogado instantaneamente.`
      );
      closeModal();
      await fetchSubscriptions();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedSub) return;
    try {
      setActionLoading(true);
      setError('');
      await api.patch(`/admin/subscriptions/${selectedSub.id}/admin-status`, {
        status: 'active',
        payment_status: 'paid',
        admin_notes: adminNotes || 'Subscrição reativada manualmente por administrador',
      });
      setSuccessBanner(
        `Subscrição de ${selectedSub.client?.company_name || 'Cliente'} reativada com sucesso! Acesso Kong restaurado.`
      );
      closeModal();
      await fetchSubscriptions();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const renderPaymentBadge = (paymentStatus?: string) => {
    switch (paymentStatus) {
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            Pago
          </span>
        );
      case 'pending_approval':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 animate-pulse">
            <Clock className="w-3 h-3 text-amber-500" />
            Aprovação Pendente
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
            <XCircle className="w-3 h-3 text-rose-500" />
            Falhado
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
            <Clock className="w-3 h-3 text-slate-400" />
            Pendente
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-950/60 border border-primary-200 dark:border-primary-800 flex items-center justify-center text-primary-600 dark:text-primary-400">
            <CreditCard className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              Gestão de Subscrições & Pagamentos
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Aprovação manual de comprovativos bancários, ativação de planos, suspensão e controlo
              de acessos Kong Gateway.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button onClick={fetchSubscriptions} className="btn-base btn-md btn-secondary gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
          <Link to="/payment-methods" className="btn-base btn-md btn-secondary gap-2">
            <CreditCard className="w-4 h-4" />
            <span>Métodos de Pagamento</span>
          </Link>
          <Link to="/clients" className="btn-base btn-md btn-primary gap-2">
            <Building2 className="w-4 h-4" />
            <span>Gerir Clientes</span>
          </Link>
        </div>
      </div>

      {successBanner && (
        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 p-4 text-sm text-emerald-800 dark:text-emerald-300 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <span>{successBanner}</span>
          </div>
          <button
            onClick={() => setSuccessBanner('')}
            className="text-emerald-600 hover:text-emerald-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 p-4 text-sm text-rose-800 dark:text-rose-300 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 text-rose-600 dark:text-rose-400" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-rose-600 hover:text-rose-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Total Subscrições
            </p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.total}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Registadas na base</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
            <Layers className="w-5 h-5" />
          </div>
        </div>

        {/* Pendentes de Aprovação */}
        <div
          onClick={() => {
            setPaymentStatusFilter('pending_approval');
            setStatusFilter('all');
          }}
          className={`cursor-pointer transition-all bg-white dark:bg-slate-900 rounded-2xl p-5 border shadow-sm flex items-center justify-between ${
            stats.pendingApprovals > 0
              ? 'border-amber-300 dark:border-amber-700 bg-amber-50/20 dark:bg-amber-950/10 hover:shadow-md'
              : 'border-slate-200 dark:border-slate-800'
          }`}
          title="Clique para filtrar apenas subscrições que requerem aprovação"
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
              Aprovação Pendente
            </p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {stats.pendingApprovals}
              </p>
              {stats.pendingApprovals > 0 && (
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                </span>
              )}
            </div>
            <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-1">
              Aguardando confirmação
            </p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center">
            <Clock className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Subscrições Ativas
            </p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
              {stats.active}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Com ACL Kong ativa</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Apps Vinculadas
            </p>
            <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">
              {stats.connectedApps}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Credenciais ativas</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
            <Activity className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Receita Mensal Base
            </p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
              {formatCurrency(stats.mrr)}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Soma planos ativos</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 flex items-center justify-center">
            <DollarSign className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pesquisar por cliente, email, API, app, método de pagamento..."
            className="input pl-10"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Status da Subscrição */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="select w-auto min-w-[150px]"
          >
            <option value="all">Todos os Estados</option>
            <option value="active">Ativas</option>
            <option value="cancelled">Canceladas / Suspensas</option>
            <option value="expired">Expiradas</option>
          </select>

          {/* Estado do Pagamento */}
          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="select w-auto min-w-[170px]"
          >
            <option value="all">Todos Pagamentos</option>
            <option value="pending_approval">Aprovação Pendente</option>
            <option value="paid">Pagos</option>
            <option value="pending">Pendentes</option>
            <option value="failed">Falhados</option>
          </select>

          {/* Ambiente */}
          <select
            value={envFilter}
            onChange={(e) => setEnvFilter(e.target.value as any)}
            className="select w-auto min-w-[150px]"
          >
            <option value="all">Todos Ambientes</option>
            <option value="sandbox">Sandbox</option>
            <option value="production">Produção</option>
          </select>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-500 dark:text-slate-400">
            A carregar subscrições e pagamentos...
          </div>
        ) : filteredSubscriptions.length === 0 ? (
          <div className="p-12 text-center space-y-2">
            <CreditCard className="w-10 h-10 text-slate-400 mx-auto" />
            <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
              Nenhuma subscrição encontrada
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Não existem subscrições correspondentes aos critérios pesquisados.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Produto API</th>
                  <th>Aplicação / Ambiente</th>
                  <th>Plano & Preço</th>
                  <th>Pagamento</th>
                  <th>Estado</th>
                  <th>Início</th>
                  <th className="text-right">Ações de Gestão</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscriptions.map((sub) => {
                  const isAwaitingApproval =
                    sub.payment_status === 'pending_approval' || sub.payment_status === 'pending';
                  const isActive = sub.status === 'active';
                  const isCancelled = sub.status === 'cancelled';

                  return (
                    <tr
                      key={sub.id}
                      className={
                        isAwaitingApproval
                          ? 'bg-amber-50/30 dark:bg-amber-950/10 hover:bg-amber-50/50 dark:hover:bg-amber-950/20'
                          : undefined
                      }
                    >
                      <td>
                        <div className="font-semibold text-slate-900 dark:text-white">
                          {sub.client?.company_name || 'Cliente'}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {sub.client?.email || sub.client_id.slice(0, 8)}
                        </div>
                      </td>

                      <td>
                        <div className="flex items-center gap-2">
                          <Box className="w-4 h-4 text-primary-500 shrink-0" />
                          <div>
                            <div className="font-semibold text-slate-900 dark:text-white">
                              {sub.product?.name || 'Produto'}
                            </div>
                            {sub.product?.authentication_provider && (
                              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                                Auth: {sub.product.authentication_provider}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td>
                        {sub.application ? (
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                              {sub.application.name}
                            </div>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                                sub.application.sandbox_mode
                                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                                  : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                              }`}
                            >
                              {sub.application.sandbox_mode ? 'Sandbox' : 'Produção'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">
                            Global (Sem App específica)
                          </span>
                        )}
                      </td>

                      <td>
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <span className="badge badge-info uppercase">
                              {sub.plan?.tier || 'Standard'}
                            </span>
                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                              {formatCurrency(Number(sub.plan?.monthly_price || 0))}/mês
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            Quota: {(sub.plan?.monthly_quota || 0).toLocaleString('pt-PT')} req •{' '}
                            {sub.plan?.rate_limit_per_minute || 60} req/min
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="space-y-1">
                          {renderPaymentBadge(sub.payment_status)}
                          <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                            <CreditCard className="w-3 h-3 text-slate-400 shrink-0" />
                            <span>
                              {sub.payment_method?.name ||
                                (sub.payment_method_id ? 'Método Registado' : 'Sem método')}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td>
                        {sub.status === 'active' ? (
                          <span className="badge badge-success gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Ativa
                          </span>
                        ) : sub.status === 'cancelled' ? (
                          <span className="badge badge-danger gap-1">
                            <XCircle className="w-3 h-3" />
                            Cancelada
                          </span>
                        ) : (
                          <span className="badge badge-warning gap-1">
                            <Clock className="w-3 h-3" />
                            Expirada
                          </span>
                        )}
                      </td>

                      <td className="text-xs text-slate-500 dark:text-slate-400">
                        {sub.start_date
                          ? new Date(sub.start_date).toLocaleDateString('pt-PT')
                          : '—'}
                      </td>

                      <td className="text-right">
                        <div className="inline-flex items-center justify-end gap-1.5 flex-wrap">
                          {/* Botão Aprovar / Confirmar Pagamento */}
                          {isAwaitingApproval && (
                            <button
                              onClick={() => openModal(sub, 'approve')}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors shadow-sm"
                              title="Confirmar Pagamento e Ativar Acesso Kong"
                            >
                              <Check className="w-3.5 h-3.5" />
                              <span>Aprovar</span>
                            </button>
                          )}

                          {/* Botão Desativar / Suspender */}
                          {isActive && (
                            <button
                              onClick={() => openModal(sub, 'deactivate')}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/50 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 transition-colors"
                              title="Desativar Subscrição e Revogar Kong"
                            >
                              <Ban className="w-3.5 h-3.5" />
                              <span>Desativar</span>
                            </button>
                          )}

                          {/* Botão Reativar */}
                          {isCancelled && (
                            <button
                              onClick={() => openModal(sub, 'reactivate')}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 transition-colors"
                              title="Reativar Subscrição e Restaurar Acesso Kong"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              <span>Reativar</span>
                            </button>
                          )}

                          {/* Botão Ver Detalhes */}
                          <button
                            onClick={() => openModal(sub, 'details')}
                            className="btn-base btn-sm btn-secondary p-1.5"
                            title="Ver Detalhes do Pagamento e Subscrição"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>

                          {/* Botão Emitir Recibo Oficial */}
                          {sub.payment_status === 'paid' && (
                            <button
                              onClick={() => setReceiptSub(sub)}
                              className="btn-base btn-sm btn-secondary p-1.5 text-emerald-600 dark:text-emerald-400"
                              title="Emitir Recibo Oficial / Proforma"
                            >
                              <FileCheck className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {sub.product_id && (
                            <Link
                              to={`/products/${sub.product_id}`}
                              className="btn-base btn-sm btn-secondary p-1.5"
                              title="Ver Produto na Loja"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ================= MODAL: APROVAR PAGAMENTO ================= */}
      {activeModal === 'approve' && selectedSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-lg w-full border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    Confirmar Pagamento & Ativar Acesso
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Aprovação manual da subscrição de API
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-4 space-y-2 border border-slate-200/60 dark:border-slate-700/60 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Cliente:</span>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {selectedSub.client?.company_name || 'Cliente'} (
                    {selectedSub.client?.email || '—'})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">API Contratada:</span>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {selectedSub.product?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Plano & Valor:</span>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                    {selectedSub.plan?.tier.toUpperCase()} —{' '}
                    {formatCurrency(Number(selectedSub.plan?.monthly_price || 0))}/mês
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Método de Pagamento:</span>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {selectedSub.payment_method?.name || 'Transferência Bancária'}
                  </span>
                </div>

                {/* Comprovativo ou Dados de Pagamento */}
                {selectedSub.payment_details && (
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700/60 text-xs text-slate-600 dark:text-slate-300">
                    <span className="font-semibold block mb-1">
                      Metadados submetidos pelo cliente:
                    </span>
                    <pre className="bg-white dark:bg-slate-950 p-2 rounded-lg font-mono text-[11px] overflow-x-auto text-slate-800 dark:text-slate-200">
                      {JSON.stringify(selectedSub.payment_details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* Informação Kong Gateway */}
              <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 rounded-xl p-3 text-xs text-emerald-800 dark:text-emerald-300 flex items-start gap-2.5">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <strong>Sincronização Kong Automática:</strong> Ao confirmar, o consumidor desta
                  aplicação será imediatamente adicionado à ACL da API e os limites de taxa serão
                  aplicados.
                </div>
              </div>

              {/* Notas do Admin */}
              <div>
                <label className="label">
                  Notas de Auditoria do Administrador (Opcional):
                </label>
                <textarea
                  rows={2}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Ex: Transferência confirmada no extrato do Banco BAI em 24/10/2026. Ref: TX-9921"
                  className="input text-sm"
                />
              </div>
            </div>

            <div className="p-6 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                disabled={actionLoading}
                className="btn-base btn-md btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleApprovePayment}
                disabled={actionLoading}
                className="btn-base btn-md btn-primary bg-emerald-600 hover:bg-emerald-700 text-white gap-2 shadow-sm"
              >
                {actionLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                <span>Confirmar e Ativar Acesso Kong</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL: DESATIVAR SUBSCRIÇÃO ================= */}
      {activeModal === 'deactivate' && selectedSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-lg w-full border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 flex items-center justify-center">
                  <ShieldAlert className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    Desativar Subscrição
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Revogação de acesso no Kong Gateway
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/60 rounded-xl p-3.5 text-xs text-rose-800 dark:text-rose-300 flex items-start gap-2.5">
                <AlertTriangle className="w-5 h-5 shrink-0 text-rose-600" />
                <div>
                  <strong>Atenção:</strong> Ao desativar esta subscrição, as credenciais da aplicação{' '}
                  <span className="font-semibold text-rose-900 dark:text-rose-200">
                    {selectedSub.application?.name || 'Cliente'}
                  </span>{' '}
                  serão removidas da lista de acesso do Kong. Chamadas às APIs serão rejeitadas
                  imediatamente com erro 403 Forbidden.
                </div>
              </div>

              <div>
                <label className="label">
                  Motivo da Desativação:
                </label>
                <select
                  value={deactivationReason}
                  onChange={(e) => setDeactivationReason(e.target.value)}
                  className="select"
                >
                  <option value="Inadimplência / Falta de Pagamento">
                    Inadimplência / Falta de Pagamento
                  </option>
                  <option value="Violação de Termos de Uso ou Rate Limit">
                    Violação de Termos de Uso ou Rate Limit
                  </option>
                  <option value="Solicitação de Cancelamento pelo Cliente">
                    Solicitação de Cancelamento pelo Cliente
                  </option>
                  <option value="Fim do Período de Validade / Rescisão">
                    Fim do Período de Validade / Rescisão
                  </option>
                  <option value="Outro Motivo Administrativo">Outro Motivo Administrativo</option>
                </select>
              </div>

              <div>
                <label className="label">
                  Notas Internas / Justificação detalhada:
                </label>
                <textarea
                  rows={3}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Explique o motivo da suspensão para registo nos logs de auditoria..."
                  className="input text-sm"
                />
              </div>
            </div>

            <div className="p-6 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                disabled={actionLoading}
                className="btn-base btn-md btn-secondary"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={handleDeactivate}
                disabled={actionLoading}
                className="btn-base btn-md btn-danger gap-2 shadow-sm"
              >
                {actionLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Ban className="w-4 h-4" />
                )}
                <span>Revogar Acesso & Desativar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL: REATIVAR SUBSCRIÇÃO ================= */}
      {activeModal === 'reactivate' && selectedSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-lg w-full border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                  <RotateCcw className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    Reativar Subscrição
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Restaurar tráfego de API no Kong Gateway
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Tem a certeza que deseja reativar a subscrição de{' '}
                <strong className="text-slate-900 dark:text-white">
                  {selectedSub.client?.company_name}
                </strong>{' '}
                no produto{' '}
                <strong className="text-slate-900 dark:text-white">
                  {selectedSub.product?.name}
                </strong>
                ? O estado do pagamento será marcado como pago e o tráfego no Kong Gateway será
                imediatamente restabelecido.
              </p>

              <div>
                <label className="label">
                  Notas de Auditoria (Opcional):
                </label>
                <textarea
                  rows={2}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Ex: Regularização confirmada em 24/10/2026."
                  className="input text-sm"
                />
              </div>
            </div>

            <div className="p-6 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                disabled={actionLoading}
                className="btn-base btn-md btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleReactivate}
                disabled={actionLoading}
                className="btn-base btn-md btn-primary bg-emerald-600 hover:bg-emerald-700 text-white gap-2 shadow-sm"
              >
                {actionLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                <span>Reativar Subscrição</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL: DETALHES DA SUBSCRIÇÃO ================= */}
      {activeModal === 'details' && selectedSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-2xl w-full border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    Detalhes da Subscrição & Pagamento
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    ID: {selectedSub.id}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto custom-scrollbar flex-1">
              {/* Badges de Estado */}
              <div className="flex items-center gap-2">
                {selectedSub.status === 'active' ? (
                  <span className="badge badge-success gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Subscrição Ativa
                  </span>
                ) : selectedSub.status === 'cancelled' ? (
                  <span className="badge badge-danger gap-1">
                    <XCircle className="w-3.5 h-3.5" />
                    Subscrição Cancelada / Suspensa
                  </span>
                ) : (
                  <span className="badge badge-warning gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    Subscrição Expirada
                  </span>
                )}
                {renderPaymentBadge(selectedSub.payment_status)}
                {selectedSub.application && (
                  <span
                    className={`badge text-xs ${
                      selectedSub.application.sandbox_mode
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                        : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                    }`}
                  >
                    {selectedSub.application.sandbox_mode ? 'Sandbox' : 'Produção'}
                  </span>
                )}
              </div>

              {/* Informações Gerais */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800 space-y-2">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase">
                    <User className="w-3.5 h-3.5" />
                    <span>Dados do Cliente</span>
                  </div>
                  <p className="font-semibold text-slate-900 dark:text-white text-base">
                    {selectedSub.client?.company_name || 'Cliente'}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Email: {selectedSub.client?.email || '—'}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Client ID: {selectedSub.client_id}
                  </p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800 space-y-2">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase">
                    <Box className="w-3.5 h-3.5" />
                    <span>Produto & Aplicação</span>
                  </div>
                  <p className="font-semibold text-slate-900 dark:text-white text-base">
                    {selectedSub.product?.name || 'Produto'}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Aplicação: {selectedSub.application?.name || 'Global'}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    App ID: {selectedSub.application_id || '—'}
                  </p>
                </div>
              </div>

              {/* Plano e Quotas */}
              <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase">
                    <DollarSign className="w-3.5 h-3.5" />
                    <span>Plano Contratado</span>
                  </div>
                  <span className="badge badge-info uppercase">
                    {selectedSub.plan?.tier || 'Standard'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-white dark:bg-slate-900 p-2.5 rounded-lg border border-slate-200/60 dark:border-slate-700">
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">Preço Mensal</p>
                    <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
                      {formatCurrency(Number(selectedSub.plan?.monthly_price || 0))}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-slate-900 p-2.5 rounded-lg border border-slate-200/60 dark:border-slate-700">
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">Quota Mensal</p>
                    <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
                      {(selectedSub.plan?.monthly_quota || 0).toLocaleString('pt-PT')} req
                    </p>
                  </div>
                  <div className="bg-white dark:bg-slate-900 p-2.5 rounded-lg border border-slate-200/60 dark:border-slate-700">
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">Rate Limit</p>
                    <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
                      {selectedSub.plan?.rate_limit_per_minute || 60} req/min
                    </p>
                  </div>
                </div>
              </div>

              {/* Método e Detalhes de Pagamento */}
              <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800 space-y-3">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase">
                  <CreditCard className="w-3.5 h-3.5" />
                  <span>Método & Dados de Pagamento</span>
                </div>
                <div className="text-sm space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">Método Registado:</span>
                    <span className="font-semibold text-slate-900 dark:text-white">
                      {selectedSub.payment_method?.name || 'Não especificado'} (
                      {selectedSub.payment_method?.type || '—'})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">Estado de Pagamento:</span>
                    <span>{renderPaymentBadge(selectedSub.payment_status)}</span>
                  </div>
                </div>

                {/* Dados do Comprovativo / JSON */}
                {selectedSub.payment_details && (
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700/60 text-xs">
                    <p className="font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Metadados & Histórico de Auditoria:
                    </p>
                    <pre className="bg-white dark:bg-slate-950 p-3 rounded-lg font-mono text-[11px] overflow-x-auto text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-800">
                      {JSON.stringify(selectedSub.payment_details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* Datas de Vigência */}
              <div className="grid grid-cols-2 gap-4 text-xs text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                  <span>
                    Início:{' '}
                    <strong className="text-slate-700 dark:text-slate-300">
                      {selectedSub.start_date
                        ? new Date(selectedSub.start_date).toLocaleString('pt-PT')
                        : '—'}
                    </strong>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                  <span>
                    Término:{' '}
                    <strong className="text-slate-700 dark:text-slate-300">
                      {selectedSub.end_date
                        ? new Date(selectedSub.end_date).toLocaleString('pt-PT')
                        : 'Ativo Indefinidamente'}
                    </strong>
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                {selectedSub.payment_status === 'pending_approval' && (
                  <button
                    type="button"
                    onClick={() => {
                      setActiveModal('approve');
                    }}
                    className="btn-base btn-sm btn-primary bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>Aprovar Pagamento</span>
                  </button>
                )}
                {selectedSub.status === 'active' && (
                  <button
                    type="button"
                    onClick={() => {
                      setActiveModal('deactivate');
                    }}
                    className="btn-base btn-sm btn-danger gap-1.5"
                  >
                    <Ban className="w-3.5 h-3.5" />
                    <span>Desativar</span>
                  </button>
                )}
                {selectedSub.status === 'cancelled' && (
                  <button
                    type="button"
                    onClick={() => {
                      setActiveModal('reactivate');
                    }}
                    className="btn-base btn-sm btn-secondary gap-1.5"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Reativar</span>
                  </button>
                )}
                {selectedSub.payment_status === 'paid' && (
                  <button
                    type="button"
                    onClick={() => {
                      setReceiptSub(selectedSub);
                    }}
                    className="btn-base btn-sm btn-secondary text-emerald-600 dark:text-emerald-400 gap-1.5"
                  >
                    <FileCheck className="w-3.5 h-3.5" />
                    <span>Emitir Recibo Oficial</span>
                  </button>
                )}
              </div>
              <button type="button" onClick={closeModal} className="btn-base btn-md btn-secondary">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Recibo Oficial Administrativo */}
      <SubscriptionReceiptModal
        isOpen={!!receiptSub}
        onClose={() => setReceiptSub(null)}
        subscription={receiptSub as any}
      />
    </div>
  );
};
